/**
 * Authenticated API client.
 *
 * Every request carries a fresh App Bridge session token. Before this the
 * frontend called /api/* with no credentials at all, which only worked because
 * the server had no authentication.
 *
 * App Bridge is loaded from Shopify's CDN in index.html (required -- it must be
 * the CDN copy, not a bundled one), which exposes window.shopify.
 */

export class ApiError extends Error {
  constructor(message, { status, reauthorize = false, hpCode = null, code = null, billing = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.reauthorize = reauthorize;
    this.hpCode = hpCode;
    // Set on a 402 from the quota gate; `billing` is the full entitlement.
    this.code = code;
    this.billing = billing;
  }

  /** True when this failed because the shop is out of its monthly labels. */
  get isQuotaExceeded() {
    return this.code === 'QUOTA_EXCEEDED';
  }
}

/** True when running inside the Shopify admin with App Bridge available. */
export function hasAppBridge() {
  return typeof window !== 'undefined' && typeof window.shopify?.idToken === 'function';
}

async function sessionToken() {
  if (!hasAppBridge()) return null;
  try {
    return await window.shopify.idToken();
  } catch (err) {
    console.error('[api] Could not get a session token from App Bridge:', err);
    return null;
  }
}

async function request(path, { method = 'GET', body, retryOnAuth = true } = {}) {
  const token = await sessionToken();

  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    // Non-JSON response (proxy error page, HTML fallback) -- fall through.
  }

  if (!res.ok || payload?.success === false) {
    const message = payload?.error || `Request failed with status ${res.status}`;

    // A 401 usually means the session token expired mid-session. App Bridge
    // will mint a new one, so retry exactly once before surfacing an error.
    if (res.status === 401 && payload?.reauthorize && retryOnAuth && hasAppBridge()) {
      return request(path, { method, body, retryOnAuth: false });
    }

    throw new ApiError(message, {
      status: res.status,
      reauthorize: Boolean(payload?.reauthorize),
      hpCode: payload?.hpCode ?? null,
      code: payload?.code ?? null,
      billing: payload?.billing ?? null,
    });
  }

  return payload;
}

function withQuery(path, params = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export const api = {
  getShop: () => request('/api/shop'),

  getOrders: (params) => request(withQuery('/api/orders', params)),
  getOrder: (id) => request(`/api/orders/${encodeURIComponent(id)}`),
  createReturnLabel: (id, options) =>
    request(`/api/orders/${encodeURIComponent(id)}/create-return-label`, { method: 'POST', body: options }),
  createShippingLabel: (id, options) =>
    request(`/api/orders/${encodeURIComponent(id)}/create-shipping-label`, { method: 'POST', body: options }),

  getLabels: (params) => request(withQuery('/api/labels', params)),
  getLabelPdf: (barcode) => request(`/api/labels/${encodeURIComponent(barcode)}/pdf`),
  // Short-lived signed link to the raw PDF, for opening it outside the iframe.
  getLabelPdfUrl: (barcode) => request(`/api/labels/${encodeURIComponent(barcode)}/pdf-url`),
  getLabelStatus: (barcode) => request(`/api/labels/${encodeURIComponent(barcode)}/status`),

  getDeliveryPoints: (facilityType, searchText) =>
    request('/api/hp/delivery-points', { method: 'POST', body: { facilityType, searchText } }),
  pingHP: () => request('/api/hp/ping', { method: 'POST' }),

  getSettings: () => request('/api/settings'),
  saveSettings: (settings) => request('/api/settings', { method: 'POST', body: settings }),

  // `refresh` forces a read-through to Shopify instead of the 5 minute cache.
  getBilling: ({ refresh = false } = {}) => request(withQuery('/api/billing', { refresh: refresh ? 1 : '' })),
};

/**
 * Sends the merchant to the Shopify-hosted plan picker.
 *
 * It must open in the *top* window: the app runs in an admin iframe, and
 * admin.shopify.com refuses to be framed, so a plain navigation renders a blank
 * panel. App Bridge intercepts `_top` navigations to admin URLs and turns them
 * into a client-side route change.
 */
export function openPricingPlans(upgradeUrl) {
  if (!upgradeUrl) return;
  try {
    window.open(upgradeUrl, '_top');
  } catch {
    window.top.location.href = upgradeUrl;
  }
}
